import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Briefcase, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntakeForm = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    industry: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    purpose: "",
    target_audience: "",
    desired_action: "",
    has_domain: "",
    domain_name: "",
    timeline: "",
    budget_range: "",
    payment_method: "",
    additional_notes: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.company_name || !formData.contact_name || !formData.email || !formData.phone || !formData.purpose) {
      toast.error("Please fill out all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/intake`, formData);
      setSubmitted(true);
      toast.success("Form submitted successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-2xl">
            <CheckCircle className="w-20 h-20 mx-auto mb-6" style={{ color: '#D4AF37' }} />
            <h1 className="text-3xl font-bold mb-4" style={{ color: '#2E2E2E' }}>Thank You!</h1>
            <p className="text-lg text-gray-700 mb-6">
              We've received your project inquiry. Our team will review your information and contact you within 24-48 hours.
            </p>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Questions? Email us at <strong style={{ color: '#D4AF37' }}>service@financialflightllc.com</strong>
              </p>
              <Button
                onClick={() => navigate("/")}
                className="transition-all hover:opacity-90"
                style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F5F5F5, #FFFFFF, #FAFAFA)' }}>
      {/* Header */}
      <header className="border-b border-gray-200 bg-white/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/financial-flight-logo.png" 
              alt="Financial Flight" 
              className="w-10 h-10"
            />
            <div>
              <span className="text-xl font-bold" style={{ color: '#2E2E2E' }}>Financial Flight</span>
              <p className="text-xs" style={{ color: '#D4AF37' }}>Web Development Services</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <Briefcase className="w-16 h-16 mx-auto mb-4" style={{ color: '#D4AF37' }} />
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
            Custom Insurance Quote Tool Development
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            For Insurance Agents & Agencies
          </p>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Get your own branded insurance quoting web application just like this one. 
            Capture leads, calculate quotes, and grow your business with a professional online presence.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-xl">
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#2E2E2E' }}>Project Inquiry Form</h2>
            <p className="text-sm text-gray-600">
              Tell us about your agency and we'll create a custom quote tool for your business.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold" style={{ color: '#2E2E2E' }}>Business Information</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name" className="text-sm font-medium">Company/Agency Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="ABC Insurance Agency"
                    required
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="industry" className="text-sm font-medium">Industry/Focus *</Label>
                  <Input
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    placeholder="Life Insurance, Auto, Home, etc."
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_name" className="text-sm font-medium">Your Name *</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@abcinsurance.com"
                    required
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website" className="text-sm font-medium">Current Website (if any)</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="www.abcinsurance.com"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold" style={{ color: '#2E2E2E' }}>Project Details</h3>
              
              <div>
                <Label htmlFor="purpose" className="text-sm font-medium">What type of quote tool do you need? *</Label>
                <Select onValueChange={(value) => handleSelectChange('purpose', value)} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Life Insurance Quote Tool">Life Insurance Quote Tool</SelectItem>
                    <SelectItem value="Auto Insurance Quote Tool">Auto Insurance Quote Tool</SelectItem>
                    <SelectItem value="Home Insurance Quote Tool">Home Insurance Quote Tool</SelectItem>
                    <SelectItem value="Health Insurance Quote Tool">Health Insurance Quote Tool</SelectItem>
                    <SelectItem value="Multi-Product Quote Tool">Multi-Product Quote Tool</SelectItem>
                    <SelectItem value="Other">Other (Specify in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="target_audience" className="text-sm font-medium">Who are your target clients?</Label>
                <Input
                  id="target_audience"
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleChange}
                  placeholder="Families, young professionals, seniors, etc."
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="desired_action" className="text-sm font-medium">What action should users take?</Label>
                <Input
                  id="desired_action"
                  name="desired_action"
                  value={formData.desired_action}
                  onChange={handleChange}
                  placeholder="Book consultation, get quote, request callback, etc."
                  className="mt-1"
                />
              </div>
            </div>

            {/* Domain & Timeline */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold" style={{ color: '#2E2E2E' }}>Timeline & Budget</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="has_domain" className="text-sm font-medium">Do you have a domain name?</Label>
                  <Select onValueChange={(value) => handleSelectChange('has_domain', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select option" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes, I have one</SelectItem>
                      <SelectItem value="No">No, need help getting one</SelectItem>
                      <SelectItem value="Preview">Use preview URL for now</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="domain_name" className="text-sm font-medium">Domain Name (if yes)</Label>
                  <Input
                    id="domain_name"
                    name="domain_name"
                    value={formData.domain_name}
                    onChange={handleChange}
                    placeholder="quotes.myagency.com"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="timeline" className="text-sm font-medium">Desired Launch Timeline</Label>
                  <Select onValueChange={(value) => handleSelectChange('timeline', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ASAP">ASAP (1-2 weeks)</SelectItem>
                      <SelectItem value="1 month">Within 1 month</SelectItem>
                      <SelectItem value="2-3 months">2-3 months</SelectItem>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="budget_range" className="text-sm font-medium">Package Selection</Label>
                  <Select onValueChange={(value) => handleSelectChange('budget_range', value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select package" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Silver - $2,997 (Web App + 3 Revisions)">
                        Silver - $2,997 (Web App + 3 Revisions)
                      </SelectItem>
                      <SelectItem value="Gold - $4,997 (Web App + 5 Revisions)">
                        Gold - $4,997 (Web App + 5 Revisions)
                      </SelectItem>
                      <SelectItem value="Platinum - $6,997 (Web App + 10 Revisions)">
                        Platinum - $6,997 (Web App + 10 Revisions)
                      </SelectItem>
                      <SelectItem value="Need more information">Need more information</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="payment_method" className="text-sm font-medium">Preferred Payment Method</Label>
                <Select onValueChange={(value) => handleSelectChange('payment_method', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PayPal">PayPal</SelectItem>
                    <SelectItem value="Zelle">Zelle</SelectItem>
                    <SelectItem value="Cash App">Cash App</SelectItem>
                    <SelectItem value="Debit/Credit Card">Debit/Credit Card</SelectItem>
                    <SelectItem value="Klarna/Afterpay">Klarna / Afterpay (Payment Plan)</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer / Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-4 pt-6 border-t border-gray-200">
              <div>
                <Label htmlFor="additional_notes" className="text-sm font-medium">Additional Details or Questions</Label>
                <Textarea
                  id="additional_notes"
                  name="additional_notes"
                  value={formData.additional_notes}
                  onChange={handleChange}
                  placeholder="Tell us about any specific features you need, integrations, or questions you have..."
                  rows={5}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: '#D4AF37', color: '#2E2E2E' }}
              >
                {loading ? "Submitting..." : "Submit Project Inquiry"}
              </Button>
              <p className="text-xs text-center text-gray-500 mt-3">
                We'll respond within 24-48 hours with a custom proposal
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default IntakeForm;